import { Button, Collapse, Spin, Tabs, Tooltip } from "antd";
import AppLoader from "components/AppLoader";
import React, { useCallback, useMemo, useRef, useState } from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import JiraMappingForm from "./jira/JiraMappingForm";
import ModalUtil from "utils/modalUtil";
import _, { isEmpty } from "lodash";
import { PermissionSetMap } from "constants/RBACConstants";
import { EditIcon, InfoIcon } from "components/Icons";
import { SyncCMDBResourceButton, useCmdbSupportedIntegrations, useTemplateSupportedIntegrations } from "./genericIntegrationTicketUtils";
import TableContainerEvolved from "components/TableContainerEvolved";
import {
  panelHeader,
} from "./jira/JiraListingNew";
import ActionMenuV2 from "components/ActionMenuV2";
import message from "components/CustomMessage";
import TicketTemplateForm from "./TicketTemplateForm";
import { CaretDownOutlined } from "@ant-design/icons";
import { ToggleButton } from "./IntegrationsTable";
import Vocabulary from "components/Vocabulary";
import { CellRenderers, columnRenderArrayData } from "utils/tableUtils";
import Text from "antd/lib/typography/Text";
import { getOperationalStatusFilterText } from "./common";
import AuthorisedContainer from "components/AuthorisedContainer";
import useUrlFilters from "hooks/useUrlFilters";
import JiraCMDBConfigListing from "./JiraCMDB/JiraCMDBConfigListing";
import ServiceNowCmdbMappingForm, { ServiceNowCmdbConfigListingTable } from "./serviceNow/ServiceNowCmdbListing";
import LoadingIcon from "ac-components/ac-icons/LoadingIcon";
import JiraCMDBMappingForm from "./JiraCMDB/JiraCMDBMappingForm";
import { useJiraCMDBData } from "./JiraCMDB/JiraCMDBUtils";

function GenericIntegrationTicketSystemListing({
  loginConfigList,
  loading,
  ...props
}) {
  const cmdbSupportedTools = useCmdbSupportedIntegrations(props.name);
  const showSyncCMDBResourceButton = cmdbSupportedTools.indexOf(props.name) !== -1;

  return (
    <>
    {
      showSyncCMDBResourceButton && <SyncCMDBResourceButton toolName={props.name} isDisabled={isEmpty(loginConfigList)}/>
    }
    <Spin spinning={loading} indicator={<AppLoader />}>
      {loginConfigList?.map((config, index) => (
        <ConfigItem loginConfig={config} configItemIndex={index} extraColumns={props.extraColumns} getColumns={props.getColumns} {...props} key={config.id} disableLogin={props.name === "Asana" && config.tokenType === "REQUEST_TOKEN"}/>
      ))}
    </Spin>
    </>
  );
}

export default GenericIntegrationTicketSystemListing;

// resolve TS with required props names
const ConfigItem = ({
  loginConfig,
  deleteAPI,
  enabledAPI,
  name,
  onUpdateConfigComponent,
  getColumns,
  extraColumns,
  disableLogin = false,
  ...props
}) => {
  const configTableRef = useRef();
  const urlFilters = useUrlFilters();
  const [configCount, setConfigCount] = useState(loginConfig?.configCount || 0);
  let defaultActiveLoginConfigId = null;

  if(!isEmpty(urlFilters)){
    defaultActiveLoginConfigId = urlFilters.defaultActiveLoginConfigId;
  }

  const onEditLoginConfig = useCallback(() => {
    const ConfigLoginFormComponent = props.ModalFormComponent;
    ModalUtil.show({
      content: (
        <ConfigLoginFormComponent
          displayName={props.displayName}
          name={name}
          config={loginConfig}
          onUpdateConfigComponent={() => onUpdateConfigComponent(true)}
        />
      ),
    });
  }, [
    props.displayName,
    name,
    loginConfig,
    props.ModalFormComponent,
    onUpdateConfigComponent
  ]);

  const onDeleteConfigLogin = useCallback(async () => {
    try {
      const toolName = name.toUpperCase().replace(" ", "_");
      await ToolsIntegrationsAPI.deleteTicketSystemToolLoginDetails(
        toolName,
        loginConfig.id
      ).promise;
      onUpdateConfigComponent(true);
      message.success("Configuration deleted successfully");
    } catch (err) {
      console.log(err);
    }
  }, [loginConfig.id, name, onUpdateConfigComponent]);

  const deleteMessage = loginConfig?.configCount
    ? "Please delete the configurations of this account first"
    : "";
  const collapseActionItems = [
    {
      type: "edit",
      // for Asana, if login type is OAuth, then disable edit
      disabled: disableLogin,
    },
    {
      type: "delete",
      permission: PermissionSetMap.DELETE_TOOL,
      tooltip: deleteMessage,
      disabled: loginConfig?.configCount,
    },
  ];
  const tempateSupportedIntegrations = useTemplateSupportedIntegrations();

  if (tempateSupportedIntegrations.indexOf(name) !== -1) {
    collapseActionItems.unshift({
      type: "edit_template",
      icon: <EditIcon />,
      text: (
        <>
          Edit Ticket Template
          <Tooltip title="Template will be applied to all configurations where the template has not been modified.">
            <InfoIcon className="m-l-xs" />
          </Tooltip>
        </>
      ),
    });
  }

  const onCollapseActionClick = async (_, record) => {
    switch (record.name) {
      case "edit":
        onEditLoginConfig();
        break;
      case "delete":
        await onDeleteConfigLogin();
        break;
      case "edit_template":
        ModalUtil.show({
          content: (
            <TicketTemplateForm isConfigLevel configId={loginConfig.id} />
          ),
          afterClose: onUpdateConfigComponent,
        });
        break;
      default:
        break;
    }
  };

  return (
    <Collapse
      className="pull-based-tool"
      defaultActiveKey={
        defaultActiveLoginConfigId ? defaultActiveLoginConfigId[0] : undefined
      }
      bordered={false}
      expandIconPosition="right"
      expandIcon={({ isActive }) => (
        <Tooltip title={isActive ? "Close Mapping" : "Open Mapping"}>
          <CaretDownOutlined
            rotate={isActive ? 180 : 0}
            style={{ fontSize: 20 }}
          />
        </Tooltip>
      )}
    >
      <Collapse.Panel
        key={loginConfig.id}
        header={panelHeader(
          loginConfig,
          configCount,
        )}
      >
        <ConfigListingPanelContent
          collapseActionItems={collapseActionItems}
          configTableRef={configTableRef}
          ConfigMappingForm={props.ConfigMappingForm}
          deleteAPI={deleteAPI}
          displayName={props.displayName}
          enabledAPI={enabledAPI}
          extraColumns={extraColumns}
          getColumns={getColumns}
          configCount={configCount}
          loginConfig={loginConfig}
          name={name}
          defaultActiveLoginConfigId={defaultActiveLoginConfigId}
          onCollapseActionClick={onCollapseActionClick}
          setConfigCount={setConfigCount}
        />
      </Collapse.Panel>
    </Collapse>
  );
};

const ConfigListingPanelContent = ({
  collapseActionItems,
  configTableRef,
  ConfigMappingForm,
  deleteAPI,
  displayName,
  enabledAPI,
  extraColumns,
  getColumns,
  configCount,
  loginConfig,
  name,
  onCollapseActionClick,
  setConfigCount,
}) => {
  const [currentTab, setCurrentTab] = useState("itsm");
  const cmdbSupportedTools = useCmdbSupportedIntegrations(name);
  const showCmdbTabs = cmdbSupportedTools.indexOf(name) !== -1;
  const {jiraCMDBData, fetchData: fetchCmdbData} = useJiraCMDBData({
    loginConfig,
    toolName: name,
  });

  const refreshConfigMappings = useCallback(() => {
    if(currentTab === "cmdb"){
      fetchCmdbData();
      return;
    }
    configTableRef.current.refresh();
    configTableRef.current.refreshFilters();
  }, [configTableRef, currentTab, fetchCmdbData]);

  const onAddConfigMapping = useCallback((data) => {
    const isEdit = Boolean(data?.id);
    const MappingFormComponent = ConfigMappingForm || JiraMappingForm;
    const CmdbMappingForm = cmdbSupportedTools.includes("Jira") ? JiraCMDBMappingForm : ServiceNowCmdbMappingForm;

    ModalUtil.show({
      content: currentTab === "itsm" ? (
        <MappingFormComponent
          name={name}
          displayName={displayName}
          config={data}
          onUpdateConfigComponent={() => {
            refreshConfigMappings();
            if (!isEdit) {
              setConfigCount((count) => count + 1);
            }
          }}
          url={name === "GitLab Issues" && loginConfig?.url}
          loginConfigId={loginConfig?.id}
        />
      ) : (
        <CmdbMappingForm
          data={data}
          loginConfigId={loginConfig?.id}
          onUpdateConfigComponent={() => {
            refreshConfigMappings();
            if (!isEdit) {
              setConfigCount((count) => count + 1);
            }
          }}
        />
      ),
    });
  }, [
    ConfigMappingForm,
    cmdbSupportedTools,
    currentTab,
    displayName,
    loginConfig?.id,
    loginConfig?.url,
    name,
    refreshConfigMappings,
    setConfigCount,
  ]);

  const CMDBListingComponent = cmdbSupportedTools.includes("Jira") ? JiraCMDBConfigListing : ServiceNowCmdbConfigListingTable;
  const tabName = cmdbSupportedTools.includes("Jira") ? "Ticketing" : "ITSM";
  const toolConfigMappingCount = configCount - (jiraCMDBData?.length ?? 0);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-flex-end m-b-sm">
        <AuthorisedContainer permission={PermissionSetMap.WRITE_TOOLS}>
          <Button size="small" type="primary" onClick={onAddConfigMapping}>
            Add Mapping
          </Button>
        </AuthorisedContainer>
        <div className="m-l-sm">
          <ActionMenuV2
            items={collapseActionItems}
            onClick={onCollapseActionClick}
          />
        </div>
      </div>
      {showCmdbTabs ? (
        <Tabs defaultActiveKey="itsm" onChange={setCurrentTab}>
          <Tabs.TabPane key="itsm" tab={`${tabName} (${toolConfigMappingCount || 0})`}>
            <Spin
              spinning={_.isUndefined(loginConfig)}
              indicator={<LoadingIcon />}
            >
              <IntegrationTicketingListingTable
                configTableRef={configTableRef}
                deleteAPI={deleteAPI}
                enabledAPI={enabledAPI}
                extraColumns={extraColumns}
                getColumns={getColumns}
                loginConfig={loginConfig}
                name={name}
                onAddConfigMapping={onAddConfigMapping}
                onDeleteConfigMapping={() => {
                  setConfigCount((count) => Math.max(0, count - 1));
                }}
                updateConfigListing={refreshConfigMappings}
              />
            </Spin>
          </Tabs.TabPane>
          <Tabs.TabPane key="cmdb" tab={`CMDB (${jiraCMDBData.length || 0})`}>
            <CMDBListingComponent
              data={jiraCMDBData}
              onEditConfig={onAddConfigMapping}
              onUpdateConfigComponent={() => {
                refreshConfigMappings();
                setConfigCount((count) => Math.max(0, count - 1));
              }}
              loginConfigId={loginConfig.id}
            />
          </Tabs.TabPane>
        </Tabs>
      ) : (
        <Spin
          spinning={_.isUndefined(loginConfig)}
          indicator={<LoadingIcon />}
        >
          <IntegrationTicketingListingTable
            configTableRef={configTableRef}
            deleteAPI={deleteAPI}
            enabledAPI={enabledAPI}
            extraColumns={extraColumns}
            getColumns={getColumns}
            loginConfig={loginConfig}
            name={name}
            onAddConfigMapping={onAddConfigMapping}
            onDeleteConfigMapping={() => {
              setConfigCount((count) => Math.max(0, count - 1));
            }}
            updateConfigListing={refreshConfigMappings}
          />
        </Spin>
      )}
    </div>
  );
};

export const IntegrationTicketingListingTable = ({loginConfig, extraColumns, getColumns, name, updateConfigListing, onAddConfigMapping, onDeleteConfigMapping, deleteAPI, enabledAPI, configTableRef, showTicketTemplateAction=true, apiFilterExtraParams=null}) => {
  const urlFilters = useUrlFilters();
  let defaultActiveLoginConfigId = null;
  const tempateSupportedIntegrations = useTemplateSupportedIntegrations();
      
  if(!isEmpty(urlFilters)){
    defaultActiveLoginConfigId = urlFilters.defaultActiveLoginConfigId;
  }

  const fetchData = useCallback(
    (params) => {
      if (!isEmpty(params.filters)) {
        params.filters = _.omitBy(params.filters, _.isEmpty);
      }
      params = _.omit({ ...params, ...params.filters }, [
        "filters",
        "sortColumn",
        "sortOrder",
      ]);
      const { promise, cancel } =
        ToolsIntegrationsAPI.getTicketSystemToolDetailsV2(
          loginConfig?.id,
          {
            ...params,
            // using in GitHub requires one extra param
            ...(apiFilterExtraParams || {})
          }
        );
      const updatedPromise = promise.then((response) => {
        if (response.success) {
          // setConfigData(response.data.content);
          return response.data;
        }
      });
      return { promise: updatedPromise, cancel };
    },
    [loginConfig?.id, apiFilterExtraParams]
  );

  const fetchFilters = useCallback((params) => {
    const { promise, cancel } =
      ToolsIntegrationsAPI.getTicketSystemListingFilters({
        ...params,
        loginId: loginConfig?.id, 
        // using in GitHub requires one extra param
        ...apiFilterExtraParams || {}
      });
    const updatedPromise = promise.then((response) => {
      if (response.success) {
        return { ...response.data };
      }
      return [];
    });

    return { promise: updatedPromise, cancel };
  }, [loginConfig?.id, apiFilterExtraParams]);

  const deleteConfigListItem = useCallback(
    async (record, onDelete) => {
      try {
        if (deleteAPI) {
          await deleteAPI(record.id).promise;
          onDelete?.();
          updateConfigListing();
          message.success("Configuration deleted successfully.");
        }
      } catch (err) {
        console.log(err);
      }
    },
    [updateConfigListing, deleteAPI]
  );

  const configTableActionItems = useMemo(
    () => {
      const actionArray = [
      {
        type: "edit",
      },
      {
        type: "delete",
        permission: PermissionSetMap.DELETE_TOOL,
      },
    ]
    if (tempateSupportedIntegrations.indexOf(name) !== -1 && showTicketTemplateAction) {
      actionArray.unshift({
        type: "edit_template",
        icon: <EditIcon />,
        text: "Edit Ticket Template"
      });
    }
    return actionArray
  }, [name, tempateSupportedIntegrations, showTicketTemplateAction]);

  const onConfigTableActionClick = useCallback(
    async (_, record) => {
      switch (record.name) {
        // To ensure that the lexical declaration only applies to the current case clause wrap the clauses in blocks.
        case "edit": {
          const data = { ...record.data };
          onAddConfigMapping(data);
          break;
        }
        case "edit_template":
          ModalUtil.show({
            content: <TicketTemplateForm configId={record.data.id} />,
            afterClose: () => updateConfigListing(),
          });
          break;
        case "delete":
          await deleteConfigListItem(record.data, onDeleteConfigMapping);
          break;
        default:
          break;
      }
    },
    [updateConfigListing, deleteConfigListItem, onAddConfigMapping, onDeleteConfigMapping]
  );

  const defaultFilter = useMemo(() => {
      
    const productFilter = urlFilters.defaultProductFilter?.[0];  
    const subProductFilter = urlFilters.defaultSubproductFilter?.[0];
    if (defaultActiveLoginConfigId && defaultActiveLoginConfigId[0] === loginConfig.id) {
        return {
            product: [productFilter],
            subProduct: [subProductFilter],
        };
    }
    else return {};
  }, [defaultActiveLoginConfigId, loginConfig.id, urlFilters.defaultProductFilter, urlFilters.defaultSubproductFilter]);

  const columns = useMemo(() => {
    let _columns = [
      {
        title: "Config Type",
        dataIndex: "configurationType",
        key: "configurationType",
        showFilter: true,
        showSort: true,
        render: _.capitalize,
        filterLabelFormatter: (value) => _.capitalize(value),
        width: 130,
      },
      {
        title: <Vocabulary name="product" />,
        dataIndex: "product",
        key: "product",
        showFilter: true,
        render: (text, record) => {
          // The product was moved to the archive, but its associated sub-product-level ticket configurations were not archived accordingly.
          // As a result, when the UI attempts to access these configs, the missing product reference (null) causes it to break.
          return !isEmpty(record.productNameId) ? columnRenderArrayData(_.map(_.compact(record.productNameId), (product) => product.name).join()) : <Text>All</Text>
        },
        width: 130,
      },
      {
        title: <Vocabulary name="subProduct" />,
        dataIndex: "subProduct",
        showFilter: true,
        key: "subProduct",
        render: (subProductNameIds, record) => {
          return !isEmpty(record.subProductNameIds) ? columnRenderArrayData(_.map(_.compact(record.subProductNameIds), (subproduct) => subproduct.name).join()) : <Text>All</Text>
        },
        width: 130,
      },
      {
        title: "Project Key",
        showFilter: true,
        showSort: true,
        dataIndex: "projectKey",
        key: "projectKey",
        render: CellRenderers.VALUE_OR_NA,
        width: 130
      },
      {
        title: "Issue Type",
        dataIndex: "issueType",
        width: 100,
        render: CellRenderers.VALUE_OR_NA,
      },
      ...(extraColumns ? extraColumns : []),
      {
        title: "Tool Status",
        dataIndex: "operationalStatus",
        width: 130,
        showFilter: true,
        render: (text, record) => {
          return CellRenderers.OPERATIONAL_STATUS(text, record);
        },
        filterLabelFormatter: getOperationalStatusFilterText,
      },
      {
        title: "Last Run",
        dataIndex: "lastConnected",
        key: "lastConnected",
        showSort: true,
        render: CellRenderers.TIME,
        width: 130,
      },
      {
        title: "Config",
        dataIndex: "enabled",
        key: "enabled",
        showSort: true,
        render: (text, record) => {
          return (
            <ToggleButton
              text={!text}
              record={record}
              integrationData={{ enabledAPI }}
              onUpdateConfigComponent={updateConfigListing}
            />
          );
        },
        width: 80,
      },
      {
        title: "Action",
        key: "action",
        dataIndex: "action",
        width: 50,
        render: (id, record) => {
          return (
            <ActionMenuV2
              items={configTableActionItems}
              onClick={onConfigTableActionClick}
              data={record}
            />
          );
        },
      },
    ];
    if (getColumns) {
      _columns = getColumns(_columns);
    }
    return _columns;
  }, [
    getColumns,
    configTableActionItems,
    onConfigTableActionClick,
    enabledAPI,
    updateConfigListing,
    extraColumns,
  ]);

  return <TableContainerEvolved
  api={fetchData}
  columns={columns}
  filtersApi={fetchFilters}
  enableScroll
  tableRef={(tableRef) => (configTableRef.current = tableRef)}
  useCustomPagination
  disableURLUpdate={true}
  defaultSort={{sortColumn: "configurationType", sortOrder: "ascend"}}
  defaultFilter={defaultFilter}
/> 
}
